import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@src/ui/views/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell
} from "@src/ui/views/table";
import { Skeleton } from "@src/ui/views/skeleton";
import { Avatar, AvatarImage, AvatarFallback } from "@src/ui/views/avatar";
import { MoreVertical, Trash2, UserPlus, UserMinus, Copy } from "lucide-react";
import {
  useGetTimedShuttlesInstancesQuery,
  useBookTimedShuttleInstanceMutation,
  useRemoveTimedShuttleInstanceBookingMutation,
  useDeleteTimedShuttleInstanceMutation,
} from "../../redux/timed-shuttles-api";
import { useAppSelector } from "@src/integrations/redux/redux-store";
import { selectUserFullInfo } from "@src/features/auth/redux/auth-slice";
import type { TimedShuttleInstance } from "../../timed-shuttles-models";

export function TimedShuttlesInstancesScreen() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate   = useNavigate();
  const user       = useAppSelector(selectUserFullInfo);
  const userId     = user?.userId;

  // date picker
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = React.useState(today);
  const { data, isLoading, isError, refetch } =
    useGetTimedShuttlesInstancesQuery(
      { spaceId: spaceId!, date },
      { skip: !spaceId }
    );

  const [book, { isLoading: isBooking }] = useBookTimedShuttleInstanceMutation();
  const [remove, { isLoading: isRemoving }] = useRemoveTimedShuttleInstanceBookingMutation();
  const [deleteInstance, { isLoading: isDeleting }] = useDeleteTimedShuttleInstanceMutation();

  const [selected, setSelected] = React.useState<TimedShuttleInstance|null>(null);
  const [showOptions, setShowOptions] = React.useState(false);
  const [copiedRideId, setCopiedRideId] = React.useState(false);

  // IMPORTANT: hooks must not be after conditional returns
  const copyTextToClipboard = React.useCallback(async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through
    }

    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }, []);

  const handleCopyRideId = React.useCallback(async () => {
    if (!selected?.rideId) return;
    const ok = await copyTextToClipboard(selected.rideId);
    if (ok) {
      setCopiedRideId(true);
      window.setTimeout(() => setCopiedRideId(false), 1200);
    }
  }, [copyTextToClipboard, selected?.rideId]);

  if (isLoading) return <Skeleton className="h-64 w-full"/>;
  if (isError || !data) return <p>Error loading timed-shuttle instances.</p>;

  const instances = data.data as TimedShuttleInstance[];

  const handleDelete = async () => {
    if (!selected) return;
    // confirm
    if (!confirm("Are you sure you want to delete this shuttle?")) return;
    try {
      await deleteInstance({ spaceId: spaceId!, rideId: selected.rideId }).unwrap();
      setSelected(null);
      setShowOptions(false);
      refetch();
    } catch (error) {
      console.error('Failed to delete instance:', error);
    }
  };

  const handleBook = async () => {
    if (!selected) return;
    try {
      await book({ spaceId: spaceId!, rideId: selected.rideId }).unwrap();
      setSelected(null);
      refetch();
    } catch (error) {
      console.error('Failed to book shuttle:', error);
    }
  };

  const handleRemoveBooking = async () => {
    if (!selected) return;
    try {
      await remove({ spaceId: spaceId!, rideId: selected.rideId }).unwrap();
      setSelected(null);
      refetch();
    } catch (error) {
      console.error('Failed to remove booking:', error);
    }
  };

  const isUserBooked = (inst: TimedShuttleInstance) =>
    !!userId && inst.participants.some(p => p.userId === userId);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow">
      {/* Top controls */}
      <div className="flex items-end gap-4">
        <label className="flex flex-col">
          <span className="text-sm font-medium">Date</span>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="mt-1 border-gray-300 rounded-md"
          />
        </label>
        <Button onClick={() => refetch()}>Load</Button>
        <div className="flex-1"/>
        <Button
          variant="primary"
          onClick={() => navigate(`/dashboard/${spaceId}/timed-shuttles/definitions`)}
        >
          Manage Definitions
        </Button>
      </div>

      {/* Instances table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>From → To</TableHead>
            <TableHead>Driver</TableHead>
            <TableHead className="text-right">Riders</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {instances.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-4">
                No shuttles scheduled on {date}.
              </TableCell>
            </TableRow>
          ) : (
            instances.map(inst => (
              <TableRow
                key={inst.rideId}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={()=>{ setSelected(inst); setShowOptions(false); }}
              >
                <TableCell>
                  {String(inst.hours).padStart(2,"0")}:
                  {String(inst.minutes).padStart(2,"0")}
                </TableCell>
                <TableCell>{inst.fromName} → {inst.destName}</TableCell>
                <TableCell>{inst.driverName}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end -space-x-2">
                    {inst.participants.map(p => (
                      <Avatar key={p.partId} className="h-6 w-6 rounded-full border-2 border-white">
                        <AvatarImage src={p.profilePic??""} alt={p.firstName}/>
                        <AvatarFallback>{p.firstName.charAt(0)??"?"}</AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-96 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0">
                <h3 className="text-xl font-semibold">
                  Riders for {selected.fromName} → {selected.destName} @{" "}
                  {String(selected.hours).padStart(2,"0")}:
                  {String(selected.minutes).padStart(2,"0")}
                </h3>

                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 font-mono break-all">
                  <span>rideId: {selected.rideId}</span>
                  <button
                    type="button"
                    onClick={handleCopyRideId}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100"
                    title="Copy rideId"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {copiedRideId && <span className="font-sans text-[11px] text-green-600">Copied</span>}
                  </button>
                </div>
              </div>

              {/* options button + dropdown */}
              <div className="relative">
                <button
                  onClick={e=>{ e.stopPropagation(); setShowOptions(o=>!o); }}
                  className="p-1 rounded hover:bg-gray-200"
                >
                  <MoreVertical className="h-5 w-5"/>
                </button>

                {showOptions && (
                  <div
                    className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-md z-10"
                    onClick={e=>e.stopPropagation()}
                  >
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 w-full text-left disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4 text-red-600"/>
                      {isDeleting ? "Deleting…" : "Delete Instance"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {selected.participants.length > 0 ? (
              <div className="space-y-3">
                {selected.participants.map(p=>(
                  <div key={p.partId} className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={p.profilePic??""} alt={p.firstName}/>
                      <AvatarFallback>{p.firstName.charAt(0)??"?"}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{p.firstName} {p.lastName}</div>
                      {p.participantNotes && (
                        <div className="text-sm text-muted-foreground">{p.participantNotes}</div>
                      )}
                      {p.status && (
                        <div className="text-sm text-muted-foreground">Status: {p.status}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No riders joined yet.</p>
            )}

            <div className="mt-6 flex justify-between items-center">
              {/* Book/Remove buttons on the left */}
              <div className="flex gap-2">
                {isUserBooked(selected) ? (
                  <Button
                    onClick={handleRemoveBooking}
                    disabled={isRemoving}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <UserMinus className="h-4 w-4 text-red-600"/>
                    {isRemoving ? "Removing…" : "Remove Booking"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleBook}
                    disabled={isBooking}
                    variant="primary"
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4"/>
                    {isBooking ? "Booking…" : "Book Shuttle"}
                  </Button>
                )}
              </div>
              
              {/* Close button on the right */}
              <Button
                onClick={() => {
                  setSelected(null);
                  setCopiedRideId(false);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}